/**
 * Shared, framework-free helpers for Luna chat file attachments.
 *
 * Limits and allowed types must stay in sync with the portal server gate:
 *   5 files/message, 4.5 MB/file, 50 MB/conversation (the conversation cap is
 *   enforced server-side and surfaced via the send:message reply).
 *
 * Allowed types on the portal path: PDF, CSV, TXT, PNG, JPG/JPEG, WebP, GIF,
 * and Office (DOCX, XLSX). Office files are run through server-side text
 * extraction (OfficeText) before persisting.
 */

export const MAX_FILE_SIZE = 4_500_000;
export const MAX_FILES_PER_MESSAGE = 5;

// Extension → canonical MIME. We always derive the content-type from the
// extension because browsers report "", application/vnd.ms-excel, or
// application/octet-stream for CSV/Office files, and the server validates
// content_type against the extension.
const EXTENSION_TO_MIME: Record<string, string> = {
  ".pdf": "application/pdf",
  ".csv": "text/csv",
  ".txt": "text/plain",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".gif": "image/gif",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xlsx":
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

export const ALLOWED_EXTENSIONS = Object.keys(EXTENSION_TO_MIME);

// Value for the file picker's `accept` attribute.
export const FILE_ACCEPT_ATTR = ALLOWED_EXTENSIONS.join(",");

type FileLike = { name: string; type: string };
type SizedFileLike = FileLike & { size: number };

const extensionOf = (filename: string): string => {
  const lower = filename.toLowerCase();
  return lower.includes(".") ? lower.slice(lower.lastIndexOf(".")) : "";
};

/**
 * The MIME type to declare to the server and S3. Derived from the file
 * extension (falling back to the browser-reported type for unknown
 * extensions). The same value must be used for the upload-URL request, the
 * PUT Content-Type header, and the attachment payload — otherwise S3 rejects
 * the PUT with a signature error.
 */
export const mimeForFile = (file: FileLike): string =>
  EXTENSION_TO_MIME[extensionOf(file.name)] ?? file.type;

/** Returns an error message if the file is not allowed, or null if it is. */
export const validateFile = (file: SizedFileLike): string | null => {
  const ext = extensionOf(file.name);
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return `File type not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return "File exceeds 4.5 MB limit";
  }
  return null;
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const isImageContentType = (contentType: string): boolean =>
  contentType.startsWith("image/");
